import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useTextosSegmento from "../i18n/useTextosSegmento";
import Layout from "../components/Layout";
import { Dica, BotaoDica } from "../components/Dica";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import API_BASE from "../config.js";
import { getPlanoAtual } from "../services/planLimits";
import useIsMobile from "../hooks/useIsMobile";
import Nebulosa from "../components/Nebulosa";
import logoAS from "../assets/logo-as.png";

function getTenantIdDashboard() {
  try {
    const token = sessionStorage.getItem("access_token") || localStorage.getItem("access_token");
    if (!token) return (sessionStorage.getItem("tenant_id") || localStorage.getItem("tenant_id")) || "1";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return String(payload.tenant_id || (sessionStorage.getItem("tenant_id") || localStorage.getItem("tenant_id")) || "1");
  } catch {
    return (sessionStorage.getItem("tenant_id") || localStorage.getItem("tenant_id")) || "1";
  }
}

const LOCALE_POR_IDIOMA = { pt: "pt-BR", en: "en-US", es: "es-ES" };

const STATUS_COR = {
  "Recepcao":"#6366f1","Diagnostico":"#f59e0b","Aguardando Aprovacao":"#f97316",
  "Em Servico":"#3b82f6","Aguardando Pecas":"#eab308","Concluido":"#22c55e","Entregue":"#94a3b8"
};

function calcularMetrics(osOverride) {
  try {
    const tenantId = (sessionStorage.getItem("tenant_id")||localStorage.getItem("tenant_id")) || "1";
    // Correcao pontual -- vazamento entre tenants: "os_historico_v1" e uma
    // chave global (nao isolada por tenant); "os_cache_tenant_X" (mantida
    // por OS.js) e a mesma lista, so que corretamente isolada por tenant --
    // usar essa para o Dashboard nunca mostrar OS de outra oficina.
    const os = osOverride || JSON.parse(localStorage.getItem("os_cache_tenant_" + tenantId) || "[]");
    const estoque = JSON.parse(localStorage.getItem("estoque_tenant_" + tenantId) || "[]");
    const abertas = os.filter(o => !["Concluido","Entregue"].includes(o.status));
    const concluidas = os.filter(o => ["Concluido","Entregue"].includes(o.status));
    // Correcao pontual: receita deve contar apenas OS Concluido/Entregue --
    // antes somava o.total de qualquer OS (inclusive Recepcao/Diagnostico,
    // que ja chegam com total preenchido mas ainda sem aprovacao), inflando
    // a receita com valores nao confirmados. Mesma regra que o Ticket Medio
    // (concluidas.length) ja usava no denominador -- agora numerador e
    // denominador sao consistentes.
    const receitaTotal = concluidas.reduce((a, o) => a + (o.total || 0), 0);
    const receitaMes = concluidas.filter(o => {
      try { const [d,m,y] = o.criada.split(" ")[0].split("/"); const dt = new Date(y,m-1,d); const now = new Date(); return dt.getMonth()===now.getMonth()&&dt.getFullYear()===now.getFullYear(); } catch { return false; }
    }).reduce((a, o) => a + (o.total || 0), 0);
    const ticketMedio = concluidas.length > 0 ? receitaTotal / concluidas.length : 0;
    const estoqueBaixo = estoque.filter(i => i.quantidade <= i.minimo);
    const veiculosAtivos = [...new Set(abertas.map(o => o.placa))].length;
    const porStatus = {};
    os.forEach(o => { porStatus[o.status] = (porStatus[o.status] || 0) + 1; });
    const porMes = {};
    os.forEach(o => {
      try { const [d,m,y] = o.criada.split(" ")[0].split("/"); const k = m+"/"+y; porMes[k] = (porMes[k]||0)+(o.total||0); } catch {}
    });
    const grafMes = Object.entries(porMes).sort().slice(-6).map(([k,v]) => ({ mes: k, faturamento: v }));
    const porDia = {};
    const hoje = new Date();
    for (let i=6; i>=0; i--) {
      const d = new Date(hoje); d.setDate(d.getDate()-i);
      const k = d.toLocaleDateString("pt-BR",{day:"2-digit",month:"2-digit"});
      porDia[k] = 0;
    }
    os.forEach(o => {
      try { const k = o.criada.split(" ")[0].substring(0,5); if(porDia[k]!==undefined) porDia[k]++; } catch {}
    });
    const grafDia = Object.entries(porDia).map(([k,v]) => ({ dia: k, os: v }));
    return { abertas: abertas.length, concluidas: concluidas.length, receitaTotal, receitaMes, ticketMedio, estoqueBaixo, veiculosAtivos, porStatus, grafMes, grafDia, totalOS: os.length };
  } catch { return { abertas:0, concluidas:0, receitaTotal:0, receitaMes:0, ticketMedio:0, estoqueBaixo:[], veiculosAtivos:0, porStatus:{}, grafMes:[], grafDia:[], totalOS:0 }; }
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  // AUD-E: t() sensivel ao segmento. Usado SO nas chamadas de
  // vocabulario de veiculo; o resto da tela segue no t() de sempre.
  const { tSeg } = useTextosSegmento();
  const localeAtual = LOCALE_POR_IDIOMA[i18n.language] || "pt-BR";
  const [metrics, setMetrics] = useState(calcularMetrics());
  const [featuresLiberadas, setFeaturesLiberadas] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dev_features_liberadas_tenant_" + getTenantIdDashboard()) || "[]"); } catch { return []; }
  });
  const temChecklist = getPlanoAtual() === "premium" || featuresLiberadas.includes("checklist");
  const isMobile = useIsMobile();

  useEffect(() => {
    setMetrics(calcularMetrics());
    const t = setInterval(() => setMetrics(calcularMetrics()), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const tenantId = getTenantIdDashboard();
    const token = sessionStorage.getItem("access_token") || localStorage.getItem("access_token") || "";
    fetch(API_BASE + "/api/v1/config/features_liberadas_tenant_" + tenantId, { headers: token ? { Authorization: "Bearer " + token } : {} })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && d.value && Array.isArray(d.value.features)) {
          setFeaturesLiberadas(d.value.features);
          try { localStorage.setItem("dev_features_liberadas_tenant_" + tenantId, JSON.stringify(d.value.features)); } catch {}
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const tenantId = getTenantIdDashboard();
    const token = sessionStorage.getItem("access_token") || localStorage.getItem("access_token") || "";
    // Busca ao vivo do backend para popular/atualizar o cache tenant-scoped que calcularMetrics()
    // le -- corrige o dashboard mostrando 0 quando o usuario ainda nao visitou a tela de O.S.
    // nesta sessao do navegador (cache os_cache_tenant_X ainda vazio).
    fetch(API_BASE+"/api/v1/dados/os/"+tenantId, {headers:{Authorization:"Bearer "+token}})
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (Array.isArray(d)) {
          try { localStorage.setItem("os_cache_tenant_" + tenantId, JSON.stringify(d)); } catch (e) { console.warn("os_cache setItem failed (non-fatal):", e); }
          setMetrics(calcularMetrics(d));
        }
      })
      .catch(() => {});
  }, []);

  const pieData = Object.entries(metrics.porStatus).map(([s,v]) => ({ name:s, value:v, cor: STATUS_COR[s]||"#6366f1" }));

  return (
    <Layout>
      <Nebulosa />
      {/* Marca d'água discreta (~8%) do logo no canto do dashboard --
          igual à referência (fusao-grid-particulas.html). */}
      <div aria-hidden="true" style={{
        position: "fixed", right: 24, bottom: 24, width: 190, height: 190, zIndex: -1,
        backgroundImage: `url(${logoAS})`, backgroundRepeat: "no-repeat",
        backgroundPosition: "center", backgroundSize: "contain",
        opacity: 0.08, pointerEvents: "none",
      }} />
      <div style={{display:"flex",flexDirection:"column",gap:20}}>

        <div style={{alignItems: isMobile ? "flex-start" : "center",display:"flex",flexDirection: isMobile ? "column" : "row",gap: isMobile ? 4 : 0,justifyContent:"space-between"}}>
          <div>
            <div style={{alignItems:"center",display:"flex",gap:8}}><div style={{fontSize:22,fontWeight:800}}>{t("dashboard.titulo")}</div><BotaoDica id="dashboard" /></div>
            <div style={{color:"var(--text2)",fontSize:13,marginTop:4}}>{t("dashboard.subtitulo")}</div>
          </div>
          <div style={{color:"var(--text3)",fontSize:12}}>{new Date().toLocaleDateString(localeAtual,{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</div>
        </div>

        <Dica id="dashboard" jaUsou={false} texto={t("dicas.dashboard")} />

        <div
          onClick={()=>navigate("/checklist")}
          style={{
            alignItems: isMobile ? "flex-start" : "center", background:"linear-gradient(120deg,#4338ca 0%,#6366f1 55%,#0ea5e9 100%)",
            borderRadius:16, cursor:"pointer", display:"flex", flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 14 : 20, justifyContent:"space-between",
            overflow:"hidden", padding: isMobile ? "20px" : "22px 28px", position:"relative", transition:"transform 0.2s,box-shadow 0.2s",
          }}
          onMouseOver={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 12px 32px -8px rgba(79,70,229,0.55)";}}
          onMouseOut={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow="none";}}
        >
          <div style={{alignItems:"center",background:"rgba(255,255,255,0.14)",borderRadius:"50%",display:"flex",flexShrink:0,height:52,justifyContent:"center",width:52}}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
          </div>
          <div style={{flex:1,minWidth:0}}>
            <span style={{background:"rgba(255,255,255,0.16)",borderRadius:99,color:"#fff",fontSize:10,fontWeight:700,letterSpacing:"0.06em",padding:"3px 10px",textTransform:"uppercase"}}>
              {temChecklist ? t("dashboard.checklist_cta_badge") : t("dashboard.checklist_cta_badge_premium")}
            </span>
            <div style={{color:"#fff",fontSize:18,fontWeight:800,marginTop:8}}>{t("dashboard.checklist_cta_titulo")}</div>
            <div style={{color:"rgba(255,255,255,0.85)",fontSize:13,marginTop:4,maxWidth:560}}>{tSeg("dashboard.checklist_cta_desc")}</div>
          </div>
          <button
            onClick={(e)=>{e.stopPropagation();navigate("/checklist");}}
            style={{alignItems:"center",background:"#fff",border:"none",borderRadius:10,color:"#4338ca",cursor:"pointer",display:"flex",flexShrink:0,fontSize:13,fontWeight:700,gap:8,justifyContent:"center",padding:"12px 20px",whiteSpace:"nowrap",width: isMobile ? "100%" : "auto"}}
          >
            {temChecklist ? t("dashboard.checklist_cta_botao") : t("dashboard.checklist_cta_botao_premium")}
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#4338ca" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
          </button>
        </div>

        <div style={{display:"grid",gap:16,gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)"}}>
          {[
            { label:t("dashboard.os_abertas"),   valor: metrics.abertas,      cor:"#6366f1", sub:t("dashboard.em_andamento"),     icon:"#", link:"/os" },
            { label:t("dashboard.concluidas"),   valor: metrics.concluidas,    cor:"#22c55e", sub:t("dashboard.total_historico"),  icon:"!", link:"/os" },
            { label:t("dashboard.receita_mes"),  valor:"R$ "+metrics.receitaMes.toLocaleString(localeAtual,{minimumFractionDigits:2}), cor:"#3b82f6", sub:t("dashboard.faturamento_mensal"), icon:"$", link:"/financeiro" },
            { label:t("dashboard.ticket_medio"), valor:"R$ "+metrics.ticketMedio.toLocaleString(localeAtual,{minimumFractionDigits:2,maximumFractionDigits:2}), cor:"#f59e0b", sub:t("dashboard.por_os_concluida"),  icon:"~", link:"/financeiro" },
          ].map(c => (
            <div key={c.label} onClick={()=>navigate(c.link)} className="card decor-fundo" style={{cursor:"pointer",transition:"transform 0.2s"}}
              onMouseOver={e=>e.currentTarget.style.transform="translateY(-2px)"}
              onMouseOut={e=>e.currentTarget.style.transform="none"}>
              <div style={{alignItems:"center",display:"flex",justifyContent:"space-between",marginBottom:12}}>
                <span style={{color:"var(--text2)",fontSize:11,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase"}}>{c.label}</span>
                <div style={{background:c.cor+"22",borderRadius:8,fontSize:16,padding:"6px 8px"}}>{c.icon}</div>
              </div>
              <div style={{fontSize:28,fontWeight:800,marginBottom:4}}>{c.valor}</div>
              <div style={{color:"var(--text3)",fontSize:12}}>{c.sub}</div>
            </div>
          ))}
        </div>

        <div style={{display:"grid",gap:16,gridTemplateColumns: isMobile ? "1fr" : "1fr 380px"}}>
          <div className="card">
            <div style={{fontSize:13,fontWeight:700,letterSpacing:"0.06em",marginBottom:20,textTransform:"uppercase"}}>{t("dashboard.faturamento_por_mes")}</div>
            {metrics.grafMes.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={metrics.grafMes}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="mes" stroke="var(--text3)" fontSize={11} />
                  <YAxis stroke="var(--text3)" fontSize={11} tickFormatter={v=>"R$"+v} />
                  <Tooltip formatter={v=>["R$ "+Number(v).toLocaleString(localeAtual,{minimumFractionDigits:2}),t("dashboard.faturamento_por_mes")]} />
                  <Line type="monotone" dataKey="faturamento" stroke="#3b82f6" strokeWidth={2} dot={{fill:"#3b82f6"}} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{alignItems:"center",color:"var(--text3)",display:"flex",fontSize:13,height:220,justifyContent:"center"}}>{t("dashboard.crie_os_grafico")}</div>
            )}
          </div>

          <div className="card">
            <div style={{fontSize:13,fontWeight:700,letterSpacing:"0.06em",marginBottom:20,textTransform:"uppercase"}}>{t("dashboard.os_por_status")}</div>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({name,value})=>value}>
                    {pieData.map((e,i) => <Cell key={i} fill={e.cor} />)}
                  </Pie>
                  <Tooltip />
                  <Legend formatter={(v)=><span style={{fontSize:11,color:"var(--text2)"}}>{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{alignItems:"center",color:"var(--text3)",display:"flex",fontSize:13,height:220,justifyContent:"center"}}>{t("dashboard.nenhuma_os_ainda")}</div>
            )}
          </div>
        </div>

        <div style={{display:"grid",gap:16,gridTemplateColumns: isMobile ? "1fr" : "1fr 340px"}}>
          <div className="card">
            <div style={{fontSize:13,fontWeight:700,letterSpacing:"0.06em",marginBottom:20,textTransform:"uppercase"}}>{t("dashboard.os_abertas_7_dias")}</div>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={metrics.grafDia}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="dia" stroke="var(--text3)" fontSize={11} />
                <YAxis stroke="var(--text3)" fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="os" stroke="#6366f1" strokeWidth={2} dot={{fill:"#6366f1"}} name="OS" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="card">
            <div style={{alignItems:"center",display:"flex",justifyContent:"space-between",marginBottom:16}}>
              <div style={{fontSize:13,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase"}}>{t("dashboard.estoque_baixo")}</div>
              {metrics.estoqueBaixo.length > 0 && <span style={{background:"#ef444422",borderRadius:99,color:"#ef4444",fontSize:11,fontWeight:700,padding:"2px 8px"}}>{metrics.estoqueBaixo.length} {t("dashboard.itens")}</span>}
            </div>
            {metrics.estoqueBaixo.length === 0 ? (
              <div style={{color:"var(--text3)",fontSize:13,padding:"20px 0",textAlign:"center"}}>{t("dashboard.sem_estoque_baixo")}</div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {metrics.estoqueBaixo.map((i,idx) => (
                  <div key={idx} style={{alignItems:"center",borderTop:idx>0?"1px solid var(--border)":"none",display:"flex",justifyContent:"space-between",padding:"10px 0"}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:600}}>{i.nome}</div>
                      <div style={{color:"var(--text3)",fontSize:11}}>{i.categoria}</div>
                    </div>
                    <span style={{background:"#ef444422",borderRadius:6,color:"#ef4444",fontSize:12,fontWeight:700,padding:"2px 8px"}}>{i.quantidade} {i.unidade}</span>
                  </div>
                ))}
              </div>
            )}
            <button onClick={()=>navigate("/estoque")} style={{background:"transparent",border:"1px solid var(--border)",borderRadius:8,color:"var(--text2)",cursor:"pointer",fontSize:12,marginTop:16,padding:"8px 0",width:"100%"}}>{t("dashboard.ver_estoque")}</button>
          </div>
        </div>

        <div className="card">
          <div style={{fontSize:13,fontWeight:700,letterSpacing:"0.06em",marginBottom:16,textTransform:"uppercase"}}>{t("dashboard.resumo_geral")}</div>
          <div style={{display:"grid",gap:16,gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)"}}>
            {[
              { label:t("dashboard.total_os"),       valor: metrics.totalOS,        cor:"#6366f1" },
              { label:tSeg("dashboard.veiculos_patio"), valor: metrics.veiculosAtivos,  cor:"#3b82f6" },
              { label:t("dashboard.receita_total"),  valor:"R$ "+metrics.receitaTotal.toLocaleString(localeAtual,{minimumFractionDigits:2}), cor:"#22c55e" },
              { label:t("dashboard.itens_em_falta"), valor: metrics.estoqueBaixo.length, cor:"#ef4444" },
            ].map(r => (
              <div key={r.label} style={{background:"var(--bg3)",borderRadius:10,padding:16,textAlign:"center"}}>
                <div style={{color:r.cor,fontSize:26,fontWeight:800,marginBottom:4}}>{r.valor}</div>
                <div style={{color:"var(--text2)",fontSize:12}}>{r.label}</div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </Layout>
  );
}
